import HttpRequest from '@/config/axios.config'
import { INSPECTION_PARAMS_TYPE, INSPECTION_PAYLOAD_TYPE } from '@/constant/qsf'
import { getUsersRef } from '@/screens/SystemConfigure/UserManagment/UserManagment.service'
import { isEmptyValue } from '@/utils/common'
import { get, map, pick } from 'lodash'
import {
  COMMENT_INSPECTION,
  INSPECTION_COMMENT_PARAM_TYPE,
  REQUEST_REVISION,
} from './Inspection.constant'

export async function getInspections(params: INSPECTION_PARAMS_TYPE) {
  return await HttpRequest.get('/inspections/search', { params })
}

export async function getInspectionsAdvance(
  params: string,
  payload: INSPECTION_PAYLOAD_TYPE = {}
) {
  const url = `/inspections/search-advance?${params}`
  return await HttpRequest.post(url, payload)
}

export async function getInspectionAdvanceSummary(
  payload: INSPECTION_PAYLOAD_TYPE = {}
) {
  const defaultValue = {
    total: 0,
    total_active: 0,
    total_submitted: 0,
  }

  try {
    const {
      data: { data = defaultValue },
    } = await HttpRequest.post(`/inspections/search-advance-summary`, payload)

    return data
  } catch (error) {
    return defaultValue
  }
}

export async function getOrderNumbers(orderNumber: string) {
  return await HttpRequest.get(`/orders/search/${orderNumber}`)
}

// suspend => use getOrderNumbersByFilter instead
// export async function getOrderNumbersByStyleId(style_ids: any) {
//   return await HttpRequest.get(`/orders/property-value/styleId/${style_ids}`)
// }

export async function getOrderNumbersByFilter(
  params,
  { orderNumber = null, styleIds = [], supplierName = null },
  signal = null
) {
  try {
    const { pageNumber = 1, pageSize = 10 } = params
    const payload = {
      number: orderNumber,
      style_ids: styleIds,
      supplier_id: supplierName,
    }

    const {
      data: { data = [], statusCode, pagination = {} },
    } = await HttpRequest.post(
      `/orders/filter?page_number=${pageNumber}&page_size=${pageSize}`,
      payload,
      { signal }
    )
    const result = { data, pagination, statusCode }
    return result
  } catch (error) {
    return { data: [], statusCode: 200, pagination: {} }
  }
}

export async function getPropertiesByFields(fields: string[]) {
  try {
    const { data } = await HttpRequest.get('/properties', {
      params: { fields: fields.join(',') },
    })
    return data?.data ?? []
  } catch (error) {
    console.error(error)
    throw error
  }
}

export async function getInspectionTypes() {
  return await HttpRequest.get('/properties/inspection_type')
}

export async function getInspectionTemplatesByInspectionType(
  inspectionTypeId: string
) {
  return await HttpRequest.get(
    `/templates/inspection_types/${inspectionTypeId}`
  )
}

export async function getAllInspectors() {
  try {
    const pickField = ['id', 'first_name', 'last_name']

    const payload = {
      all: true,
      status: 'active',
    }
    const { data } = await getUsersRef(payload)
    const result = map(data.data, (item) => pick(item, pickField))

    return result
  } catch (error) {
    console.log(error)
  }
}

export async function createInspection(inspector: any) {
  return await HttpRequest.post('/inspections/create2', inspector)
}

export async function createReInspection(inspector: any) {
  return await HttpRequest.post('/inspections/re-inspections', inspector)
}

export async function updateInspection(inspector: any) {
  return await HttpRequest.patch('/inspections/update_assignment', inspector)
}

export async function deleteInspection(id: string) {
  return await HttpRequest.delete(`/inspections/${id}`)
}

export async function deleteApprovedInspection(id: string) {
  return await HttpRequest.delete(`/inspections/${id}/approved`)
}

export async function inspectionDetail(id: string) {
  return await HttpRequest.get(`/inspections/${id}`)
}

type SUPPLIER_COUNTRY = {
  type?: 'supplier' | 'factory'
  companyId: string
  pickField?: Array<'id' | 'name' | 'country' | 'factories'>
  status?: 'active' | 'inactive'
  all?: boolean
}

// TODO remove isAdmin later
export async function getSupplierRef(
  {
    companyId = '',
    type = 'supplier',
    pickField = ['id', 'name'],
  }: SUPPLIER_COUNTRY,
  isAdmin
) {
  try {
    if (!isAdmin) {
      return []
    }
    const params: SUPPLIER_COUNTRY = {
      status: 'active',
      all: true,
      type: type,
      ...(!isEmptyValue(companyId) && { companyId }),
    }
    const { data } = await HttpRequest.get(`/suppliers`, { params })

    const filter = map(data.data, (item) => pick(item, pickField))
    return filter
  } catch (error) {
    console.log(error)
  }
}

export async function supplierRef(params: any) {
  return await HttpRequest.get('/suppliers', { params: params })
}

// TODO remove isAdmin later
export async function getFactoriesRef(
  {
    companyId = '',
    type = 'factory',
    pickField = ['id', 'name'],
  }: SUPPLIER_COUNTRY,
  isAdmin
) {
  try {
    if (!isAdmin) {
      return []
    }
    const params: SUPPLIER_COUNTRY = {
      status: 'active',
      all: true,
      type: type,
      ...(!isEmptyValue(companyId) && { companyId }),
    }
    const { data } = await HttpRequest.get(`/suppliers`, { params })

    const filter = map(data.data, (item) => pick(item, pickField))
    return filter
  } catch (error) {
    console.log(error)
  }
}

// TODO remove isAdmin later
export async function getCountryByFactory(
  { companyId = '', type = 'factory' }: SUPPLIER_COUNTRY,
  isAdmin
) {
  try {
    if (!isAdmin) {
      return []
    }
    const params: SUPPLIER_COUNTRY = {
      status: 'active',
      all: true,
      type: type,
      ...(!isEmptyValue(companyId) && { companyId }),
      pickField: ['id', 'name'],
    }
    const { data } = await HttpRequest.get(`/suppliers/countries`, { params })
    return data.data
  } catch (error) {}
}

export async function postInspectionComment(
  params: INSPECTION_COMMENT_PARAM_TYPE
) {
  const { payload, inspectionId } = params

  const queryString = `/comments/inspection/${inspectionId}`
  const { data } = await HttpRequest.post(queryString, payload)

  return data.data
}

export type GET_SECTION_COMMENT_TYPE = {
  commentType: 'section' | 'sub_section' | 'task' | 'inspection'
  id: string
}

export type REQUEST_REVISION_PAYLOAD_TYPE = {
  inspectionId: string
  payload: { comment?: string; mentions?: []; medias?: []; files?: [] }
}

export async function getSectionCommentListService(
  params: GET_SECTION_COMMENT_TYPE
) {
  if (!params || !params?.id) {
    return
  }

  const { id, commentType } = params
  const queryStaring = `/comments/${commentType}/${id}`
  const { data } = await HttpRequest.get(queryStaring)

  return data.data
}

export async function postRequestRevisionApi(
  params: REQUEST_REVISION_PAYLOAD_TYPE
) {
  try {
    const { payload, inspectionId } = params
    const getComment = get(payload, 'comment', '')
    let wrapPayload = {}

    if (getComment) {
      wrapPayload = {
        type: 'new',
        json_value: { ...payload },
        comment_type: COMMENT_INSPECTION,
        category: REQUEST_REVISION,
      }
    }
    const queryString = `/inspections/${inspectionId}/request_revision`
    const { data } = await HttpRequest.post(queryString, wrapPayload)

    return data.data
  } catch (error) {
    throw error
  }
}

export async function postApproveSampleApi(params: any) {
  try {
    const { inspectionId } = params
    const queryString = `/inspections/${inspectionId}/sample-approval`
    const { data } = await HttpRequest.patch(queryString)

    return data.data
  } catch (error) {
    throw error
  }
}
